// Generate a PDF for an "Acción de Personal" document.
// Strategy: render the HTML inside an off-screen A4 container with the company
// header (centred name + logo top-right), rasterise with html2canvas, then
// stitch the resulting image into a multi-page jsPDF. This keeps rich-text
// formatting (bold, alignment, lists, colours) and the ₡ symbol intact.
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { sanitizeHtml } from "@/lib/sanitize";

const A4_W_MM = 210;
const A4_H_MM = 297;
const MARGIN_MM = 14;

function detectImageFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const m = dataUrl.match(/^data:image\/(png|jpeg|jpg|webp);base64,/i);
  if (!m) return null;
  return m[1].toLowerCase() === "jpg" ? "JPEG" : m[1].toUpperCase();
}

function buildPrintNode({ empresa, html, docTitle, includeEmpresaHeader = true }) {
  const root = document.createElement("div");
  root.setAttribute("data-acciones-print-root", "true");
  Object.assign(root.style, {
    position: "fixed", left: "-9999px", top: "0",
    width: "794px",                          // ~ A4 width @ 96dpi
    minHeight: "1123px",
    background: "#ffffff", color: "#1c2733",
    fontFamily: "'Figtree', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "14px", lineHeight: "1.55",
    padding: "60px 70px",
    boxSizing: "border-box",
  });

  if (includeEmpresaHeader) {
    const header = document.createElement("div");
    header.style.cssText = "display:flex;align-items:center;justify-content:center;position:relative;margin-bottom:30px;padding-bottom:18px;border-bottom:1px solid #d6dbe1;";
    const name = document.createElement("div");
    name.style.cssText = "font-family:'Outfit',serif;font-size:22px;font-weight:700;text-align:center;letter-spacing:0.3px;";
    name.textContent = empresa?.razon_social || empresa?.nombre || "Empresa";
    header.appendChild(name);

    if (empresa?.logo && detectImageFormat(empresa.logo)) {
      const logo = document.createElement("img");
      logo.src = empresa.logo;
      logo.style.cssText = "position:absolute;right:0;top:-10px;max-height:70px;max-width:130px;object-fit:contain;";
      header.appendChild(logo);
    }
    root.appendChild(header);
  }

  if (docTitle) {
    const t = document.createElement("div");
    t.style.cssText = "font-family:'Outfit',serif;font-size:12px;color:#6b7480;text-align:center;margin-bottom:18px;letter-spacing:0.6px;text-transform:uppercase;";
    t.textContent = docTitle;
    root.appendChild(t);
  }

  const body = document.createElement("div");
  body.className = "acciones-body";
  body.innerHTML = sanitizeHtml(html);
  // Normalise paragraph spacing
  body.querySelectorAll("p").forEach(p => { p.style.margin = "0 0 12px 0"; });
  body.querySelectorAll("h1,h2,h3").forEach(h => { h.style.margin = "0 0 14px 0"; });
  root.appendChild(body);
  return root;
}

export async function generarAccionPDF({ empresa, html, fileName, docTitle, includeEmpresaHeader = true, returnBase64 = true }) {
  const node = buildPrintNode({ empresa, html, docTitle, includeEmpresaHeader });
  document.body.appendChild(node);
  try {
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const pageW = A4_W_MM - MARGIN_MM * 2;
    const ratio = canvas.height / canvas.width;
    const imgH = pageW * ratio;
    const pageH = A4_H_MM - MARGIN_MM * 2;
    if (imgH <= pageH) {
      pdf.addImage(imgData, "JPEG", MARGIN_MM, MARGIN_MM, pageW, imgH);
    } else {
      // Split into multiple A4 pages
      const totalPages = Math.ceil(imgH / pageH);
      const sliceCanvas = document.createElement("canvas");
      const ctx = sliceCanvas.getContext("2d");
      const sliceHpx = Math.floor((pageH / imgH) * canvas.height);
      sliceCanvas.width = canvas.width;
      for (let p = 0; p < totalPages; p++) {
        const remaining = canvas.height - p * sliceHpx;
        sliceCanvas.height = Math.min(sliceHpx, remaining);
        ctx.clearRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, p * sliceHpx, canvas.width, sliceCanvas.height,
                      0, 0, sliceCanvas.width, sliceCanvas.height);
        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
        const sliceMM = (sliceCanvas.height / canvas.width) * pageW;
        if (p > 0) pdf.addPage();
        pdf.addImage(sliceData, "JPEG", MARGIN_MM, MARGIN_MM, pageW, sliceMM);
      }
    }
    pdf.save(fileName);
    const result = { fileName };
    if (returnBase64) {
      const uri = pdf.output("datauristring");
      result.pdfBase64 = uri.split(",")[1];
    }
    return result;
  } finally {
    node.remove();
  }
}
